import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bug, Lightbulb, MessageSquarePlus, X, Send, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const TYPES = [
  { id: "bug", label: "Bug / Problem", icon: Bug, tone: "text-red-300 bg-red-500/10 border-red-500/40", hint: "Something broken or acting up" },
  { id: "suggestion", label: "Suggestion", icon: Lightbulb, tone: "text-amber-300 bg-amber-500/10 border-amber-500/40", hint: "New feature or idea" },
  { id: "improvement", label: "Improvement", icon: Sparkles, tone: "text-emerald-300 bg-emerald-500/10 border-emerald-500/40", hint: "Refine something existing" },
];

/**
 * Feedback / Report a problem widget. Admin and staff surface a floating trigger
 * in the sidebar footer; on click a dialog collects type + title + description.
 * The current URL is captured so triagers know what surface the report is about.
 */
export default function FeedbackDialog() {
  const { user } = useAuth();
  const canReport = ["org_admin", "production_staff", "super_admin"].includes(user?.role);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "bug", title: "", description: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open]);

  if (!canReport) return null;

  const submit = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error("Title and description are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/feedback", { ...form, page: typeof window !== "undefined" ? window.location.pathname + window.location.search : "" });
      toast.success("Thanks — your feedback was logged");
      setOpen(false);
      setForm({ type: "bug", title: "", description: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit feedback");
    } finally { setSaving(false); }
  };

  return (
    <>
      <button
        data-testid="report-problem-btn"
        onClick={() => setOpen(true)}
        className="w-9 h-9 rounded-md border border-white/10 grid place-items-center text-zinc-300 hover:text-gold hover:border-gold/40 hover:bg-gold/5 transition-colors"
        title="Report a problem or suggest an improvement"
      >
        <MessageSquarePlus className="w-4 h-4" />
      </button>

      {open && (
        <div className="fixed inset-0 z-[85]" data-testid="report-problem-dialog">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg rounded-2xl border border-white/10 bg-[#0F0F0F] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] overflow-hidden">
            <header className="px-5 py-4 border-b border-white/10 flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-gold">Feedback</div>
                <h2 className="mt-0.5 font-display text-2xl leading-tight">Report a problem or suggestion</h2>
                <p className="text-[11px] text-zinc-500 mt-1">Everything you send here is reviewed by the platform admins.</p>
              </div>
              <button data-testid="report-problem-close" onClick={() => setOpen(false)} className="w-8 h-8 grid place-items-center rounded border border-white/10 text-zinc-400 hover:text-white shrink-0"><X className="w-4 h-4" /></button>
            </header>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Type</div>
                <div className="grid grid-cols-3 gap-2">
                  {TYPES.map(({ id, label, icon: Icon, tone, hint }) => (
                    <button
                      key={id}
                      data-testid={`report-type-${id}`}
                      onClick={() => setForm({ ...form, type: id })}
                      className={`text-left rounded-lg border p-2.5 transition-colors
                        ${form.type === id ? tone : "border-white/10 text-zinc-400 hover:border-white/25"}`}
                    >
                      <Icon className="w-4 h-4" />
                      <div className="mt-1.5 text-sm">{label}</div>
                      <div className="text-[10px] opacity-80 mt-0.5 leading-tight">{hint}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1.5">Title</div>
                <input
                  data-testid="report-title-input"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full h-10 px-3 rounded-md bg-[#0A0A0A] border border-white/10 focus:border-gold/40 text-sm outline-none"
                  placeholder="One-line summary"
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1.5">Details</div>
                <textarea
                  data-testid="report-description-input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={6}
                  className="w-full px-3 py-2 rounded-md bg-[#0A0A0A] border border-white/10 focus:border-gold/40 text-sm outline-none"
                  placeholder={form.type === "bug"
                    ? "What did you expect? What happened? Any steps to reproduce?"
                    : "Describe your idea and the outcome you'd love to see"}
                />
              </div>
              <div className="text-[10px] text-zinc-500">
                We&apos;ll attach your name, email, role, and current page ({typeof window !== "undefined" ? window.location.pathname : ""}) so triagers can follow up.
              </div>
            </div>
            <footer className="px-5 py-4 border-t border-white/10 flex items-center justify-end gap-2 bg-[#0A0A0A]/60">
              <button onClick={() => setOpen(false)} className="h-9 px-3 rounded border border-white/15 hover:border-white/30 text-sm text-zinc-200">Cancel</button>
              <button
                data-testid="report-submit-btn"
                onClick={submit}
                disabled={saving}
                style={{ backgroundColor: "#F59E0B", color: "#0A0A0A" }}
                className="h-9 px-4 rounded font-semibold text-sm inline-flex items-center gap-1.5 hover:brightness-110 disabled:opacity-60"
              >
                <Send className="w-3.5 h-3.5" /> {saving ? "Sending…" : "Send feedback"}
              </button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}
